import React, { useState, useEffect } from "react";
import axios from "axios";
import { useUser } from "../../context/UserContext";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Invoice.css';
import { QRCodeSVG } from "qrcode.react";
import {
  FileText,
  DollarSign,
  Trash2,
  Tag,
  BadgePercent,
  User,
  IdCard,
  UserRound,
  Cake,
  XCircle,
  Stethoscope
} from 'lucide-react'
import { motion } from "framer-motion";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceQRcode = () => {
  const [patientDetails, setPatientDetails] = useState(null);
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const invoice_number = searchParams.get("invoice_number");
  const token = searchParams.get("token");
  const [invalidData, setInvalidData] = useState(false);
  const [service, setServiceOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [systemData, setSystemData] = useState({
    phoneNumber: '',
    tinNumber: '',
    address: '',
    email: '',
    titleAmharic: '',
    titleEnglish: '',
  });

  useEffect(() => {
    // Fetch the current settings from the backend
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/setting/get-settings`);
        setSystemData(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchSettings();
  }, []);


  useEffect(() => {
    fetch(`${BASE_URL}/patient/view-all-patients-based-on-ward/${patientId}`)
      .then((res) => res.json())
      .then((data) => {
        setPatientDetails(data);
      })
      .catch((err) => console.error("Error fetching patient details:", err));
  }, [patientId]);

  useEffect(() => {
    if (!token || !invoice_number || !patientId) {
      setInvalidData(true);
      setLoading(false);
      return;
    }

    const fetchService = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/client/service-invoice-qrcode`, {
          params: { invoice_number, patient_id: patientId, token }
        });

        if (!response.data || response.data.length === 0) {
          setInvalidData(true);
        } else {
          setServiceOrder(response.data);
        }
      } catch (error) {
        console.error("Error fetching service data:", error);
        setInvalidData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [token, invoice_number, patientId]);

  useEffect(() => {
    const calculatedTotal = service.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + price * quantity;
    }, 0);
    setTotalPrice(calculatedTotal);
    setGrandTotal(calculatedTotal - (calculatedTotal * discount) / 100);
  }, [service, discount]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500">
        Loading...
      </div>
    );
  }


  // 🚨 Invalid Data UI
  if (invalidData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-green-50">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center p-8 rounded-2xl shadow-lg bg-white border border-green-300"
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
            className="flex justify-center"
          >
            <XCircle className="w-20 h-20 text-green-600" />
          </motion.div>
          <h2 className="mt-4 text-2xl font-bold text-green-700">Invalid Data</h2>
          <p className="text-green-600 mt-2">The service order for this invoice could not be found.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] w-full md:min-h-screen bg-gray-100 mt-24 px-2 sm:px-4">
      <ToastContainer />
      <div className="flex-grow text-right">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 hidden-print"
        >
          Print
        </button>
      </div>
      <div className="max-w-7xl mx-auto p-2 bg-white border border-black rounded-lg shadow-md print-invoice">
        {/* Logo and Header Section */}
        <div className="text-center">
          <img src="/assets/hiywot_logo.jpg" alt="hiywot_logo" className="mx-auto h-24" />
          <div className="text-lg">
            <p className="text-base font-bold text-gray-800">
              <span className="font-semibold text-blue-600">Email:</span> {systemData.email} |

              <span className="font-bold text-blue-600">
                TIN <span className="underline">No</span>:
              </span>  {systemData.tinNumber} |

              <span className="font-bold text-blue-600">
                Phone <span className="underline">No</span> :
              </span> {systemData.phoneNumber} |
              <span className="font-bold text-blue-600"> Address:</span> {systemData.address}
            </p>
            <h2 className="text-center text-lg underline font-bold text-gray-900">
              Service Payment Details
            </h2>
          </div>
        </div>
        {patientDetails && (
          <div className="flex flex-wrap gap-6 mb-4 mt-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" />
              <p className="font-bold">Client name:</p>
              <p>{patientDetails.name} {patientDetails.fname} {patientDetails.gfname}</p>
            </div>
            <div className="flex items-center gap-2">
              <IdCard className="w-4 h-4 text-green-600" />
              <p className="font-bold">MRN:</p>
              <p>{patientDetails.mrn}</p>
            </div>
            <div className="flex items-center gap-2">
              <UserRound className="w-4 h-4 text-pink-500" />
              <p className="font-bold">Sex:</p>
              <p>{patientDetails.sex}</p>
            </div>
            <div className="flex items-center gap-2">
              <Cake className="w-4 h-4 text-yellow-600" />
              <p className="font-bold">Age:</p>
              <p>{patientDetails.age}</p>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-6 flex-wrap mb-2">
          {/* Invoice Section */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 rounded border border-black p-1 w-24">
              <FileText className="w-4 h-4" />
              <p className="whitespace-nowrap font-bold">Invoice</p>
            </div>
            <input
              className="rounded border border-black p-1 w-28"
              value={invoice_number}
              readOnly
            />
          </div>

        </div>
        {/* 🌈 Gradient Separator Line */}
        <div className="h-1 w-full rounded-full mb-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="table-auto w-full rounded-lg shadow">
                <thead className="bg-gray-400">
                  <tr>
                    <th className="py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        <span>#</span>
                      </div>
                    </th>
                    <th className="py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        <BadgePercent className="w-4 h-4 text-indigo-700" />
                        <span>Service</span>
                      </div>
                    </th>
                    <th className="py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        #
                        <span>Quantity</span>
                      </div>
                    </th>
                    <th className="py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-indigo-700" />
                        <span>Price</span>
                      </div>
                    </th>
                    <th className="py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-indigo-700" />
                        <span>Total</span>
                      </div>
                    </th>

                    <th className="hidden-print py-1 px-1 text-left">
                      <div className="flex items-center gap-2">
                        <Trash2 className="w-4 h-4 text-indigo-700" />
                        <span>Remove</span>
                      </div>
                    </th>

                  </tr>
                </thead>
                <tbody>
                  {service.map((test, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-200 ${test.price === 0 ? "bg-yellow-100 text-gray-700" : ""
                        }`}
                    >
                      <td className="border border-black px-1 py-1">{index + 1}</td>
                      <td className="border border-black px-1 py-1">{test.service_name}</td>
                      <td className="border border-black px-1 py-1">{test.quantity}</td>
                      <td className="border border-black px-1 py-1">{(Number(test.price) || 0).toFixed(2)}</td>

                      <td className="border border-black px-1 py-1">
                        {((Number(test.price) || 0) * (parseInt(test.quantity) || 1)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between py-2">
                <div className="h-[10%] w-[10%]">
                  <QRCodeSVG
                    value={`https://amenmediumclinic.com/service-payment-details?invoice_number=${invoice_number}&patientId=${patientId}`}
                    size={128}
                  />
                </div>

                {/* Rounded Table-like Container */}
                <div className="relative bg-white rounded-lg w-fit min-h-full overflow-hidden before:absolute before:top-0 before:left-0 before:h-full before:w-2 before:rounded-tr before:rounded-br before:bg-gradient-to-b before:from-pink-500 before:via-purple-500 before:to-blue-500">
                  <table className="w-full border-collapse ml-2">
                    <tbody>
                      {/* Subtotal Row */}
                      <tr className="border-b border-gray-300">
                        <td className="text-left font-semibold text-sm text-gray-900">Subtotal</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                          {(Number(totalPrice) || 0).toFixed(2)}
                        </td>
                      </tr>
                      {/* VAT Row */}
                      <tr className="border-b border-gray-300">
                        <td className="text-left font-semibold text-sm text-gray-900">VAT</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                          0.00 ETB
                        </td>
                      </tr>

                      <tr>
                        <td className="text-left font-semibold text-sm text-gray-900">Grand Total</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                          {(Number(grandTotal) || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ServiceQRcode;
